import type {RouteLocationNormalized, Router} from 'vue-router';

import {toRaw, unref} from 'vue';
import {defineStore} from 'pinia';
import {store} from '@/store';

import {useGo} from '@/hooks/web/usePage';
import {Persistent} from '@/utils/cache/persistent';

import {PageEnum} from '@/enums/pageEnum';
import {PAGE_NOT_FOUND_ROUTE, REDIRECT_ROUTE} from '@/router/routes/basic';
import {getRawRoute} from '@/utils';
import {MULTIPLE_TABS_KEY} from '@/enums/cacheEnum';

import projectSetting from '@/settings/projectSetting';
import {useUserStore} from '@/store/modules/user';
import {StorageUtils} from 'xmgoing-utils';
import {isQianKunApp} from "@/migo/apps";
import {clearAllStores} from "@/store/utils/dynamicStore";
import eventBus from "@/events/eventBus";
import {PAGE_NOT_FOUND_NAME_SON} from "@/router/constant";
import GoingUtils from "@/utils/goingUtils";

export interface MultipleTabState {
  delPageList: any;
  cacheTabList: Set<string>;
  tabList: RouteLocationNormalized[];
  lastDragEndIndex: number;
}

function handleGotoPage(router: Router) {
  const go = useGo(router);
  go(unref(router.currentRoute).path, true);
}

const getToTarget = (tabItem: RouteLocationNormalized) => {
  const {params, path, query} = tabItem;
  return {
    params: params || {},
    path,
    query: query || {},
  };
};

const cacheTab = projectSetting.multiTabsSetting.cache;

/** 页签关闭：结束该页签下打开详情部件抽屉；联查页签同时清联查页签缓存。切走再切回不走这里。 */
function notifyTabsClosed(tabs: Array<{ fullPath?: string; path?: string }>) {
  for (const tab of tabs) {
    const fullPath = String(tab.fullPath ?? '').trim();
    if (!fullPath) continue;
    eventBus.emit('closePageContentDrawerByPath', fullPath);
    if (tab.path === '/fullProcessTrace') {
      eventBus.emit('fullProcessTraceTabClosed', fullPath);
    }
  }
}

export const useMultipleTabStore = defineStore({
  id: 'app-multiple-tab',
  state: (): MultipleTabState => ({
    delPageList: {},
    // Tabs that need to be cached
    cacheTabList: new Set(),
    // multiple tab list
    tabList: cacheTab ? Persistent.getLocal(MULTIPLE_TABS_KEY) || [] : [],
    // Index of the last moved tab
    lastDragEndIndex: 0,
  }),
  getters: {
    getTabList(): RouteLocationNormalized[] {
      return this.tabList;
    },
    getCachedTabList(): string[] {
      return Array.from(this.cacheTabList);
    },
    getLastDragEndIndex(): number {
      return this.lastDragEndIndex;
    },
  },
  actions: {
    /**
     * Update the cache according to the currently opened tabs
     */
    async updateCacheTab() {
      console.warn('[DANGER] updateCacheTab 被调用！这会重置整个缓存列表！', new Error().stack);
      const cacheMap: Set<string> = new Set();
      for (const tab of this.tabList) {
        const item = getRawRoute(tab);
        // Ignore the cache
        const needCache = !item.meta?.ignoreKeepAlive;
        if (!needCache) {
          continue;
        }
        const fullPath = item.fullPath as string;
        cacheMap.add(fullPath);
      }
      this.cacheTabList = cacheMap;
      console.log('[updateCacheTab] 重置后的缓存列表:', Array.from(this.cacheTabList));
    },

    /**
     * Refresh tabs
     */
    async refreshPage(_router: Router) {
      window.location.reload();
    },
    clearCacheTabs(): void {
      this.cacheTabList = new Set();
    },
    resetState(): void {
      notifyTabsClosed(this.tabList);
      this.tabList = [];
      this.clearCacheTabs();
      // 退出登录等整体重置场景：联查页签缓存等派生缓存也随之清空，避免跨会话残留
      eventBus.emit('clearAllKeepAliveCache', {});
    },
    goToPage(router: Router) {
      const go = useGo(router);
      const len = this.tabList.length;
      const {path} = unref(router.currentRoute);

      let toPath: PageEnum | string = PageEnum.BASE_HOME;

      if (len > 0) {
        const page = this.tabList[len - 1];
        const p = page.fullPath || page.path;
        if (p) {
          toPath = p;
        }
      }
      // Jump to the current page and report an error
      path !== toPath && go(toPath as PageEnum, true);
    },

    async addTab(route: RouteLocationNormalized) {
      const {path, name, fullPath, params, query, meta} = getRawRoute(route);
      if (name === PAGE_NOT_FOUND_NAME_SON) {
        // 特殊子页面名称：保留占位，无处理逻辑
      }
      // 动态标题：把菜单名写入 meta，切换主题后不再依赖全局 curMenuName
      const dynamicTitles = ['dynamicName', '页面'];
      const metaTitle = meta?.title as string | undefined;
      const cachedTitle =
        StorageUtils.getSessionItem(path) ||
        StorageUtils.getSessionItem(fullPath) ||
        StorageUtils.getSessionItem('curMenuName');
      if (
        cachedTitle &&
        (dynamicTitles.includes(metaTitle || '') ||
          (path + '').indexOf('/xmShowPortal/') >= 0 ||
          (path + '').indexOf('/xm-erp/commonPage/') >= 0 ||
          (path + '').indexOf('/vue3Des/showPage/') >= 0 ||
          (path + '').indexOf('/xmPortalMainPage/') >= 0)
      ) {
        meta.title = cachedTitle;
      } else if ((path + "").indexOf('/xmShowPortal/') >= 0) {
        meta.title = StorageUtils.getSessionItem(path) || meta.title;
      }
      // 404  The page does not need to add a tab
      if (
        path === PageEnum.ERROR_PAGE ||
        path === PageEnum.BASE_LOGIN ||
        !name ||
        [REDIRECT_ROUTE.name, PAGE_NOT_FOUND_ROUTE.name].includes(name as string)
      ) {
        if (!meta.pageId) {
          return;
        }
      }

      // 页签绑定打开时的菜单入口（同 path 复用时用最新入口覆盖）
      const entryId = StorageUtils.getSessionItem('curMenuEntryId');
      const nextMeta: Recordable = {
        ...(meta || {}),
        ...(entryId != null && entryId !== '' ? { menuEntryId: entryId } : {}),
      };
      /**
       * 联查：openFullProcessTrace 在 push 前已把「联查 / 联查-{docNo}」写入 curMenuName，
       * 但路由 meta.title 固定为「联查」。若不在 addTab 时同步，页签首屏只显示「联查」，
       * 且页内再改 meta.title 也常刷不动 Ant Tabs 已缓存的页签文案。
       */
      if (path === '/fullProcessTrace') {
        const sessionTitle = StorageUtils.getSessionItem('curMenuName');
        if (typeof sessionTitle === 'string' && sessionTitle.startsWith('联查')) {
          nextMeta.title = sessionTitle;
        }
      }
      // 流程处理页签以流程标题命名：标题随 query.flowTitle 传入（待办中心点击待办记录打开，
      // 过长时已由调用方截取前 25 位），页签按 fullPath 去重，同一条记录 fullPath 固定 => 固定一个页签
      const dealTabTitle =
        path === '/flowList/simplFlowDeal' && query?.flowTitle ? String(query.flowTitle) : '';
      if (dealTabTitle) {
        nextMeta.title = dealTabTitle;
      }

      let updateIndex = -1;
      // Existing pages, do not add tabs repeatedly
      const tabHasExits = this.tabList.some((tab, index) => {
        updateIndex = index;
        return (tab.fullPath || tab.path) === (fullPath || path);
      });

      // If the tab already exists, perform the update operation
      if (tabHasExits) {
        const curTab = toRaw(this.tabList)[updateIndex];
        if (!curTab) {
          return;
        }
        curTab.params = params || curTab.params;
        curTab.query = query || curTab.query;
        curTab.fullPath = fullPath || curTab.fullPath;
        const metaPatch: Recordable = {};
        if (entryId != null && entryId !== '') {
          metaPatch.menuEntryId = entryId;
        }
        if (path === '/fullProcessTrace' && nextMeta.title) {
          metaPatch.title = nextMeta.title;
        }
        if (dealTabTitle) {
          metaPatch.title = dealTabTitle;
        }
        // 复用页签时同步最新标题
        if (cachedTitle && dynamicTitles.includes(String(curTab.meta?.title || metaTitle || ''))) {
          metaPatch.title = cachedTitle;
        } else if (meta?.title && meta.title !== metaTitle) {
          metaPatch.title = meta.title;
        }
        if (Object.keys(metaPatch).length) {
          curTab.meta = { ...(curTab.meta || {}), ...metaPatch };
        }
        this.tabList.splice(updateIndex, 1, curTab);
      } else {
        // Add tab
        // 获取动态路由打开数，超过 0 即代表需要控制打开数
        const dynamicLevel = nextMeta?.dynamicLevel ?? -1;
        if (dynamicLevel > 0) {
          // 如果动态路由层级大于 0 了，那么就要限制该路由的打开数限制了
          // 首先获取到真实的路由，使用配置方式减少计算开销.
          // const realName: string = path.match(/(\S*)\//)![1];
          const realPath = nextMeta?.realPath ?? '';
          // 获取到已经打开的动态路由数, 判断是否大于某一个值
          if (
            this.tabList.filter((e) => e.meta?.realPath ?? '' === realPath).length >= dynamicLevel
          ) {
            // 关闭第一个
            const index = this.tabList.findIndex((item) => item.meta.realPath === realPath);
            index !== -1 && this.tabList.splice(index, 1);
          }
        }
        this.tabList.push({
          ...route,
          meta: nextMeta,
        } as RouteLocationNormalized);
      }
      // 添加到缓存列表（仅当 meta.keepAlive 为 true 时）
      if (nextMeta?.keepAlive && !this.cacheTabList.has(fullPath)) {
        this.cacheTabList.add(fullPath);
        console.log('[Tab] 添加到缓存列表:', fullPath);
      }
      cacheTab && Persistent.setLocal(MULTIPLE_TABS_KEY, this.tabList);
    },

    /** 同 path 未触发路由变化时，仍更新页签上的菜单入口绑定 */
    bindTabMenuEntryId(pathOrFullPath: string, entryId: string | number) {
      if (entryId == null || entryId === '') return;
      const index = this.tabList.findIndex(
        (tab) =>
          tab.fullPath === pathOrFullPath ||
          tab.path === pathOrFullPath ||
          (tab.query ? tab.fullPath : tab.path) === pathOrFullPath,
      );
      if (index < 0) return;
      const curTab = toRaw(this.tabList)[index];
      curTab.meta = { ...(curTab.meta || {}), menuEntryId: entryId };
      this.tabList.splice(index, 1, curTab);
      cacheTab && Persistent.setLocal(MULTIPLE_TABS_KEY, this.tabList);
    },

    async closeTab(tab: RouteLocationNormalized, router: Router, go: any) {
      const close = (route: RouteLocationNormalized) => {
        const {fullPath, path, meta: {affix} = {}} = route;
        if (affix) {
          return;
        }
        const index = this.tabList.findIndex((item) => item.fullPath === fullPath);
        if (index !== -1) {
          this.tabList.splice(index, 1);
          this.cacheTabList.delete(fullPath);
          console.log('[Tab] 关闭tab，从缓存中移除:', fullPath);
        }
        notifyTabsClosed([{ fullPath, path }]);
      };

      const {currentRoute, replace} = router;

      // 联查等同 path 多页签，激活态与定位须用 fullPath
      const currentFullPath = unref(currentRoute).fullPath;
      if (currentFullPath !== tab.fullPath) {
        // Closed is not the activation tab
        close(tab);
        return;
      }

      // Closed is activated atb
      let toTarget: any = {};

      const index = this.tabList.findIndex((item) => item.fullPath === currentFullPath);

      // If the current is the leftmost tab
      if (index === 0) {
        // There is only one tab, then jump to the homepage, otherwise jump to the right tab
        if (this.tabList.length === 1) {
          const userStore = useUserStore();
          toTarget = userStore.getUserInfo.homePath || PageEnum.BASE_HOME;
        } else {
          //  Jump to the right tab
          const page = this.tabList[index + 1];
          toTarget = getToTarget(page);
        }
      } else {
        // Close the current tab
        const page = this.tabList[index - 1];
        toTarget = getToTarget(page);
      }
      close(tab);
      //如果乾坤子应用需要特殊处理
      if (toTarget.params && toTarget.params.path) {
        const appPath = toTarget.params.path[0];
        if (isQianKunApp(appPath)) {
          go(toTarget.path, false);
          return;
        }
      }
      await replace(toTarget);
    },

    // Close according to key
    async closeTabByKey(key: string, router: Router, go: any) {
      // 处理零代码页面的特殊逻辑
      if ((key + '').indexOf('/xm-erp/commonPage/') >= 0) {
        try {
          this.delPageList[GoingUtils.getPageCode(key)] = true;
        } catch (e) {
          // 忽略错误
        }
      }
          
      const index = this.tabList.findIndex((item) => (item.fullPath || item.path) === key);
      if (index !== -1) {
        const closedTab = this.tabList[index];
        await this.closeTab(closedTab, router, go);
        const {currentRoute, replace} = router;
        // 检查当前路由是否存在于tablist中
        const isActivated = this.tabList.findIndex((item) => {
          return item.fullPath === currentRoute.value.fullPath;
        });
        // 如果当前路由不存在于TabList中，尝试切换到其它路由
        if (isActivated === -1) {
          let pageIndex;
          if (index > 0) {
            pageIndex = index - 1;
          } else if (index < this.tabList.length - 1) {
            pageIndex = index + 1;
          } else {
            pageIndex = -1;
          }
          if (pageIndex >= 0) {
            const page = this.tabList[index - 1];
            const toTarget = getToTarget(page);
            //如果乾坤子应用需要特殊处理
            if (toTarget.params && toTarget.params.path) {
              const appPath = toTarget.params.path[0];
              if (isQianKunApp(appPath)) {
                go(toTarget.path, false);
                return;
              }
            } else {
              await replace(toTarget);
            }
          }
        }
      }
    },

    // Sort the tabs
    async sortTabs(oldIndex: number, newIndex: number) {
      const currentTab = this.tabList[oldIndex];
      this.tabList.splice(oldIndex, 1);
      this.tabList.splice(newIndex, 0, currentTab);
      this.lastDragEndIndex = this.lastDragEndIndex + 1;
    },

    // Close the tab on the right and jump
    async closeLeftTabs(route: RouteLocationNormalized, router: Router) {
      // 须用 fullPath：联查等同 path 多页签，按 path 会命中第一张
      const index = this.tabList.findIndex((item) => item.fullPath === route.fullPath);

      if (index > 0) {
        const leftTabs = this.tabList.slice(0, index);
        const pathList: string[] = [];
        for (const item of leftTabs) {
          const affix = item?.meta?.affix ?? false;
          if (!affix) {
            pathList.push(item.fullPath);
          }
        }
        this.bulkCloseTabs(pathList);
      }
      // 不再调用 updateCacheTab()，因为 bulkCloseTabs 已经处理了缓存
      handleGotoPage(router);
    },

    // Close the tab on the left and jump
    async closeRightTabs(route: RouteLocationNormalized, router: Router) {
      const index = this.tabList.findIndex((item) => item.fullPath === route.fullPath);

      if (index >= 0 && index < this.tabList.length - 1) {
        const rightTabs = this.tabList.slice(index + 1, this.tabList.length);

        const pathList: string[] = [];
        for (const item of rightTabs) {
          const affix = item?.meta?.affix ?? false;
          if (!affix) {
            pathList.push(item.fullPath);
          }
        }
        this.bulkCloseTabs(pathList);
      }
      // 不再调用 updateCacheTab()，因为 bulkCloseTabs 已经处理了缓存
      handleGotoPage(router);
    },

    async closeAllTab(router: Router) {
      const closingTabs = this.tabList.filter((item) => !(item?.meta?.affix ?? false));
      this.tabList = this.tabList.filter((item) => item?.meta?.affix ?? false);
      this.clearCacheTabs();
      notifyTabsClosed(closingTabs);
      this.goToPage(router);
      eventBus.emit('clearAllKeepAliveCache', {});
      clearAllStores();
    },

    /**
     * Close other tabs
     * 页签身份用 fullPath（联查带 query，path 相同 fullPath 不同）；勿用 path 反查。
     */
    async closeOtherTabs(route: RouteLocationNormalized, router: Router) {
      const pathList: string[] = [];
      for (const item of this.tabList) {
        if (item.fullPath === route.fullPath) continue;
        const affix = item?.meta?.affix ?? false;
        if (!affix) {
          pathList.push(item.fullPath);
        }
      }
      this.bulkCloseTabs(pathList);
      // 不再调用 updateCacheTab()，因为 bulkCloseTabs 已经处理了缓存
      handleGotoPage(router);
    },

    /**
     * Close tabs in bulk
     */
    async bulkCloseTabs(pathList: string[]) {
      const closingTabs = this.tabList.filter((item) => pathList.includes(item.fullPath));
      pathList.forEach(path => {
        this.cacheTabList.delete(path);
      });
      this.tabList = this.tabList.filter((item) => !pathList.includes(item.fullPath));
      notifyTabsClosed(closingTabs);
    },

    /**
     * Set tab's title
     */
    async setTabTitle(title: string, route: RouteLocationNormalized) {
      const findTab = this.getTabList.find((item) => item === route);
      if (findTab) {
        findTab.meta.title = title;
        await this.updateCacheTab();
      }
    },
    /**
     * replace tab's path
     * **/
    async updateTabPath(fullPath: string, route: RouteLocationNormalized) {
      const findTab = this.getTabList.find((item) => item === route);
      if (findTab) {
        findTab.fullPath = fullPath;
        findTab.path = fullPath;
        await this.updateCacheTab();
      }
    },
  },
});

// Need to be used outside the setup
export function useMultipleTabWithOutStore() {
  return useMultipleTabStore(store);
}
